import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Music, Mic, Sparkles, Star } from 'lucide-react'

const MusicFeatures = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8 transition-colors duration-200 group">
          <ArrowLeft className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          返回主页
        </Link>
        
        <div className="mb-16">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl mb-8 shadow-2xl">
            <Music className="text-white w-12 h-12" />
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-slate-900 mb-6 tracking-tight leading-tight">
            音乐功能
          </h1>
          <p className="text-2xl text-slate-600 max-w-3xl leading-relaxed">
            音乐推荐与创作，让旋律随心而动
          </p>
        </div>
        
        <div className="space-y-8">
          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Music className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">音乐推荐</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8 mb-8 border border-violet-100">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-violet-600" />
                  功能介绍
                </h3>
                <p className="text-lg leading-relaxed mb-0">
                  根据你的语音需求、场景、心情自动匹配推荐对应歌曲，覆盖儿歌、流行、纯音乐、舒缓助眠等全品类曲目。
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Music className="w-6 h-6 text-slate-600" />
                  使用示例
                </h3>
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li>"推荐几首睡前舒缓的轻音乐"</li>
                  <li>"给我推荐适合跑步听的歌"</li>
                  <li>"推荐热门流行歌曲"</li>
                  <li>"来点小朋友听的儿歌"</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Sparkles className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">音乐生成</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 mb-8 border border-purple-100">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                  功能介绍
                </h3>
                <p className="text-lg leading-relaxed mb-0">
                  输入文字描述曲风、场景、情绪、乐器等，设备实时 AI 生成专属原创背景音乐，无需联网下载歌曲，按需定制音乐片段。为优化等待体验，生成过程中设备会先以温暖人声播报AI同步生成的歌词，让用户提前感知旋律意境；若歌词播报完毕而音乐尚未生成完毕，系统将自动接续随机童话故事，以温柔陪伴填补间隙，避免枯燥等待，让每一次创作都充满惊喜与沉浸感。
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Music className="w-6 h-6 text-slate-600" />
                  使用示例
                </h3>
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li>"生成一段欢快的流行音乐"</li>
                  <li>"以'爱国'为主题生成一段音乐"</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Mic className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">歌声续写</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 mb-8 border border-pink-100">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-pink-600" />
                  功能介绍
                </h3>
                <p className="text-lg leading-relaxed mb-0">
                  支持用户即兴哼唱、自由演唱或口述旋律片段，设备内置高保真拾音麦克风精准收录人声音频，并通过AI模型实时提取旋律走向、节奏型与情绪色彩。系统结合用户长期积累的对话习惯、偏好标签与情感档案，自动生成与用户个人风格高度契合的专属歌曲。
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Mic className="w-6 h-6 text-slate-600" />
                  使用方法
                </h3>
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li>唱一段歌曲，然后说出类似指令："帮我续写一下歌曲"</li>
                  <li>等待播报结束，自动播放续写后的完整歌曲</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Star className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">演唱评测</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 mb-8 border border-amber-100">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-amber-600" />
                  功能介绍
                </h3>
                <p className="text-lg leading-relaxed mb-0">
                  收录用户演唱的音频，自动从音准、节奏、气息、完整度多维度打分评测，同步给出演唱优缺点点评、改进建议，适合练歌、儿童声乐练习。
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Star className="w-6 h-6 text-slate-600" />
                  使用方法
                </h3>
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li>唤醒后唱一首歌曲，然后说出类似指令："你觉得我唱的怎么样"</li>
                  <li>等待结束自动播报详细点评与改进建议</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MusicFeatures
